import React from 'react';
import { ReferenceLine } from 'recharts';
import { signedTransferAmount, transferDateKey } from '../../utils/transferAdjustedEquity';

/** Orange, so a transfer is not mistaken for the account line, the index, or a gridline. */
export const TRANSFER_MARK_COLOR = '#F97316';

function markerLabel(amount) {
  const abs = Math.abs(amount);
  const compact = abs >= 1000
    ? `$${(abs / 1000).toFixed(abs % 1000 === 0 ? 0 : 1)}k`
    : `$${abs.toFixed(0)}`;
  return `${amount >= 0 ? '+' : '−'}${compact}`;
}

/**
 * Net signed transfer per curve date, so a day with several moves gets one marker.
 */
export function transferMarkerPoints(transfers, chartData) {
  if (!Array.isArray(transfers) || !transfers.length || !Array.isArray(chartData)) return [];

  const curveDates = new Set(chartData.map((p) => p.time));
  const byDate = new Map();
  const seenIds = new Set();

  for (const transfer of transfers) {
    if (transfer?.id != null) {
      if (seenIds.has(transfer.id)) continue;
      seenIds.add(transfer.id);
    }
    const date = transferDateKey(transfer?.transfer_date || transfer?.transferDate || transfer?.date);
    if (!date || !curveDates.has(date)) continue;
    byDate.set(date, (byDate.get(date) || 0) + signedTransferAmount(transfer));
  }

  return [...byDate.entries()]
    .filter(([, amount]) => amount !== 0)
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([date, amount]) => ({ date, amount }));
}

/**
 * Dashed markers on the equity chart showing which day a deposit/withdrawal is applied.
 * Returned as an array of elements so Recharts still sees ReferenceLine children.
 */
export function renderTransferMarkers({ transfers, chartData, yAxisId }) {
  return transferMarkerPoints(transfers, chartData).map(({ date, amount }) => (
    <ReferenceLine
      key={`transfer-${date}`}
      yAxisId={yAxisId}
      x={date}
      stroke={TRANSFER_MARK_COLOR}
      strokeWidth={1.5}
      strokeDasharray="3 3"
      strokeOpacity={0.95}
      label={{
        value: markerLabel(amount),
        position: 'insideTop',
        fill: TRANSFER_MARK_COLOR,
        fontSize: 11,
        fontWeight: 700,
      }}
    />
  ));
}
